import {
  ALL_OTHERS_ID,
  toTargetingRows,
  type ExpressionType,
  type StatsResponse,
  type TargetingTotals,
} from './statsApi'
import {
  BREAKDOWN_ENTITY_KEY,
  isTimeBreakdown,
  objectTypeOf,
  TARGETING_BREAKDOWN,
  type Breakdown,
  type QueryObjectType,
  type SubBreakdown,
  type TimeBreakdown,
} from './statsQuery'

/** One group's metrics, keyed by metric name as the API reports them. */
export type StatsValues = Record<string, unknown>

/**
 * Which set of metrics a result carries. Publishing objects report their own
 * traffic (requests, earnings), but grouped by something from the demand side
 * the ad server answers with advertising stats instead — so the kind is read
 * off the result rather than the question.
 */
export type ResultKind = 'advertising' | 'publishing' | 'targeting'

export interface QueryRow {
  id: string
  /** Display name — an object's name, or empty for a time bucket. */
  name: string
  /** Start of the bucket, for time breakdowns. */
  time?: string
  /** Object type the row is, when it links to one. */
  objectType?: QueryObjectType
  objectId?: string
  /** For targeting rows: what kind of targeting matched. */
  targetingType?: ExpressionType
  /** The bucket everything past the top rows is summed into. */
  isOthers?: boolean
  stats: StatsValues
  subRows?: QueryRow[]
}

export interface QueryResult {
  kind: ResultKind
  rows: QueryRow[]
  totals: StatsValues
  /** Every time bucket in the period, including ones with nothing in them. */
  chunkRange: string[]
}

type Chunk = Record<string, unknown>

/** Reads the name and identity of a chunk at one level of the breakdown. */
function describe(chunk: Chunk, level: Breakdown | SubBreakdown, index: number): Omit<QueryRow, 'stats'> {
  if (isTimeBreakdown(level)) {
    const time = String(chunk.chunkStart ?? chunk.start ?? '')
    return { id: `t:${time}`, name: '', time }
  }
  const key = BREAKDOWN_ENTITY_KEY[level as keyof typeof BREAKDOWN_ENTITY_KEY]
  const entity = (chunk[key] ?? {}) as { id?: string; name?: string }
  const objectType = objectTypeOf(level as Breakdown) ?? undefined
  if (entity.id === ALL_OTHERS_ID) return { id: ALL_OTHERS_ID, name: '', isOthers: true }
  // Id 0 comes back as an empty reference: delivery nothing of that kind owns,
  // such as line items without a team.
  if (!entity.id) return { id: `none:${index}`, name: '' }
  return { id: entity.id, name: entity.name || entity.id, objectType, objectId: entity.id }
}

function numberOf(stats: StatsValues, key: string): number {
  const value = stats[key]
  if (typeof value === 'number') return value
  if (value && typeof value === 'object' && 'amount' in value) return Number((value as { amount: number }).amount) || 0
  return 0
}

/** Time buckets run in order; everything else busiest first, "all others" last. */
function sortRows(rows: QueryRow[]): QueryRow[] {
  return rows.sort((a, b) => {
    if (a.time && b.time) return new Date(a.time).getTime() - new Date(b.time).getTime()
    if (a.isOthers !== b.isOthers) return a.isOthers ? 1 : -1
    return numberOf(b.stats, 'impressions') - numberOf(a.stats, 'impressions')
  })
}

function kindOf(totals: StatsValues): ResultKind {
  return 'impressionRequests' in totals || 'positiveImpressionRequests' in totals ? 'publishing' : 'advertising'
}

/** Turns a stats response into rows for the table and chart. */
export function readStatsResult(
  response: StatsResponse,
  breakdown: Breakdown,
  subBreakdown: SubBreakdown | null
): QueryResult {
  const chunks = (response.chunks ?? []) as Chunk[]
  const rows = chunks.map((chunk, i): QueryRow => {
    const head = describe(chunk, breakdown, i)
    const inner = Array.isArray(chunk.chunks) ? (chunk.chunks as Chunk[]) : null
    if (!inner || !subBreakdown) return { ...head, stats: chunk }
    return {
      ...head,
      // Children carry their parent's id so the same site under two months
      // stays two rows.
      stats: (chunk.totals as StatsValues) ?? {},
      subRows: sortRows(
        inner.map((c, j) => {
          const child = describe(c, subBreakdown, j)
          return { ...child, id: `${head.id}/${child.id}`, stats: c }
        })
      ),
    }
  })
  const totals = (response.totals ?? {}) as StatsValues
  return {
    kind: kindOf(totals),
    rows: sortRows(rows),
    totals,
    chunkRange: ((response.chunkRange ?? []) as (string | { start: string })[]).map((entry) =>
      typeof entry === 'string' ? entry : entry.start
    ),
  }
}

/** Targeting answers with a plain map, one entry per targeting value. */
export function readTargetingResult(totals: TargetingTotals): QueryResult {
  const rows = toTargetingRows(totals).map((row, i): QueryRow => ({
    id: `${row.type}=${row.value}:${i}`,
    name: row.value,
    targetingType: row.type,
    stats: row.stats,
  }))
  return { kind: 'targeting', rows: sortRows(rows), totals: {}, chunkRange: [] }
}

export { TARGETING_BREAKDOWN }

// ─── Chart series ─────────────────────────────────────────────────────────────

export interface ChartSeries {
  key: string
  name: string
  isOthers?: boolean
}

export interface TimeSeries {
  /** Bucket size of the time axis. */
  unit: TimeBreakdown
  /** One point per time bucket: `time` plus a value per series key. */
  points: ({ time: string } & Record<string, number>)[]
  series: ChartSeries[]
}

/** How many series a chart draws before folding the rest into "Other". */
export const MAX_CHART_SERIES = 5

/**
 * Pulls a time series for one metric out of a result broken down by time. With
 * a split under each bucket, each object is a line; past `MAX_CHART_SERIES` the
 * quieter ones are summed into one "Other" line — or, for a rate, which cannot
 * be summed, left out.
 *
 * Null unless the breakdown is a time breakdown.
 */
export function toTimeSeries(
  result: QueryResult,
  breakdown: Breakdown,
  subBreakdown: SubBreakdown | null,
  metric: string,
  additive: boolean,
  nameOf: (row: QueryRow) => string
): TimeSeries | null {
  if (!isTimeBreakdown(breakdown)) return null

  // The API leaves out empty buckets; the range lists them all, so the line
  // drops to zero instead of skipping a gap.
  const buckets = result.chunkRange.length
    ? result.chunkRange
    : result.rows.flatMap((r) => (r.time ? [r.time] : [])).sort()
  const slot = new Map(buckets.map((iso, i) => [new Date(iso).getTime(), i]))
  const points = buckets.map((time) => ({ time }) as { time: string } & Record<string, number>)
  const put = (time: string | undefined, key: string, value: number) => {
    const i = time ? slot.get(new Date(time).getTime()) : undefined
    if (i !== undefined) points[i][key] = (points[i][key] ?? 0) + value
  }

  // Time alone: one line of the totals.
  if (!subBreakdown) {
    for (const row of result.rows) put(row.time, 'total', numberOf(row.stats, metric))
    return { unit: breakdown, points: fill(points, ['total']), series: [{ key: 'total', name: '' }] }
  }

  // Each series is one object, with its values across the buckets.
  const byObject = new Map<string, { name: string; isOthers?: boolean; values: [string, number][]; total: number }>()
  for (const row of result.rows) {
    for (const sub of row.subRows ?? []) {
      // Sub-row ids carry their bucket; the object is the same in every bucket.
      const id = sub.id.slice(sub.id.indexOf('/') + 1)
      const entry = byObject.get(id) ?? { name: nameOf(sub), isOthers: sub.isOthers, values: [], total: 0 }
      const value = numberOf(sub.stats, metric)
      if (row.time) entry.values.push([row.time, value])
      entry.total += value
      byObject.set(id, entry)
    }
  }

  const ranked = [...byObject.values()].sort((a, b) => {
    if (!!a.isOthers !== !!b.isOthers) return a.isOthers ? 1 : -1
    return b.total - a.total
  })
  const shown = ranked.slice(0, MAX_CHART_SERIES)
  const rest = ranked.slice(MAX_CHART_SERIES)
  const series: ChartSeries[] = shown.map((v, i) => ({ key: `s${i}`, name: v.name, isOthers: v.isOthers }))
  shown.forEach((v, i) => v.values.forEach(([time, value]) => put(time, `s${i}`, value)))
  if (rest.length && additive) {
    series.push({ key: 'other', name: '', isOthers: true })
    rest.forEach((v) => v.values.forEach(([time, value]) => put(time, 'other', value)))
  }
  return { unit: breakdown, points: fill(points, series.map((s) => s.key)), series }
}

function fill<T extends Record<string, number | string>>(points: T[], keys: string[]): T[] {
  return points.map((p) => {
    const filled: Record<string, number | string> = { ...p }
    for (const key of keys) filled[key] ??= 0
    return filled as T
  })
}
